type AnalyticsProperties = Record<
  string,
  string | number | boolean | undefined
>;

type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  queue?: unknown[][];
  version?: string;
};

type TikTokPixel = {
  _i?: Record<string, TikTokPixel>;
  _t?: Record<string, number>;
  _o?: Record<string, unknown>;
  methods?: string[];
  setAndDefer?: (target: TikTokPixel, method: string) => void;
  instance?: (id: string) => TikTokPixel;
  load?: (id: string, options?: Record<string, unknown>) => void;
  page?: () => void;
  track?: (eventName: string, properties?: AnalyticsProperties) => void;
  push?: (...args: unknown[]) => number;
};

const googleMeasurementId = (
  import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
)?.trim();
const posthogKey = (
  import.meta.env.VITE_POSTHOG_KEY as string | undefined
)?.trim();
const posthogHost = (
  import.meta.env.VITE_POSTHOG_HOST as string | undefined
)?.trim().replace(/\/$/, "") || "https://us.i.posthog.com";
const tiktokPixelId = (
  import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined
)?.trim();
const attributionStorageKey = "stalkie_attribution_v1";
const attributionMaxAgeMs = 90 * 24 * 60 * 60 * 1000;
const campaignParameterNames = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;
type CampaignParameterName = (typeof campaignParameterNames)[number];

export type AnalyticsCampaignProperties = Partial<
  Record<CampaignParameterName, string>
>;

export type CheckoutAttribution = AnalyticsCampaignProperties & {
  fbp?: string;
  fbc?: string;
  clientUserAgent?: string;
  sourceUrl?: string;
  ttp?: string;
  ttclid?: string;
};

type StoredAttribution = CheckoutAttribution & {
  capturedAt: number;
};

type PostHogModule = typeof import("posthog-js/dist/module.slim");

let posthogModulePromise: Promise<PostHogModule | undefined> | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _fbq?: MetaPixel;
    fbq?: MetaPixel;
    TiktokAnalyticsObject?: string;
    ttq?: TikTokPixel;
    __stalkieAnalyticsInitialized?: boolean;
    __stalkieMetaPixelInitialized?: boolean;
    __stalkieTikTokPixelInitialized?: boolean;
  }
}

function isProductionHost() {
  return (
    typeof window !== "undefined" &&
    (window.location.hostname === "stalkie.app" ||
      window.location.hostname === "www.stalkie.app")
  );
}

function metaPixelId() {
  const value = (
    import.meta.env.VITE_PUBLIC_META_PIXEL_ID as string | undefined
  )?.trim();
  return value && /^\d{5,20}$/.test(value) ? value : undefined;
}

function sanitizeAnalyticsPath(pathname: string) {
  if (/^\/(?:auth|app|onboarding)(?:\/|$)/.test(pathname)) {
    return pathname.split("/").slice(0, 3).join("/") || "/";
  }
  if (/^\/profile\/[^/?#]+/.test(pathname)) return "/profile/[handle]";
  return pathname || "/";
}

function sanitizeAnalyticsUrl(value: string) {
  try {
    const url = new URL(value, "https://stalkie.app");
    url.pathname = sanitizeAnalyticsPath(url.pathname);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "https://stalkie.app/";
  }
}

function currentLocationUrl() {
  if (typeof window === "undefined") return undefined;
  try {
    return new URL(window.location.href || window.location.pathname || "/", "https://stalkie.app");
  } catch {
    return undefined;
  }
}

function cleanAttributionValue(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, 200) : undefined;
}

function readCookie(name: string) {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return cleanAttributionValue(cookie?.slice(prefix.length));
}

function readStoredAttribution(): StoredAttribution {
  if (typeof window === "undefined") return { capturedAt: Date.now() };
  try {
    const raw = window.localStorage.getItem(attributionStorageKey);
    if (!raw) return { capturedAt: Date.now() };
    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    if (
      typeof parsed.capturedAt !== "number" ||
      Date.now() - parsed.capturedAt > attributionMaxAgeMs
    ) {
      return { capturedAt: Date.now() };
    }
    return {
      capturedAt: parsed.capturedAt,
      fbp: cleanAttributionValue(parsed.fbp),
      fbc: cleanAttributionValue(parsed.fbc),
      clientUserAgent: cleanAttributionValue(parsed.clientUserAgent),
      sourceUrl: cleanAttributionValue(parsed.sourceUrl),
      ttp: cleanAttributionValue(parsed.ttp),
      ttclid: cleanAttributionValue(parsed.ttclid),
      ...campaignParameterNames.reduce<AnalyticsCampaignProperties>(
        (result, parameterName) => {
          const value = cleanAttributionValue(parsed[parameterName]);
          if (value) result[parameterName] = value;
          return result;
        },
        {},
      ),
    };
  } catch {
    return { capturedAt: Date.now() };
  }
}

function captureAttribution(): StoredAttribution {
  if (typeof window === "undefined") return { capturedAt: Date.now() };

  const current = currentLocationUrl();
  const previous = readStoredAttribution();
  const next: StoredAttribution = {
    ...previous,
    capturedAt: previous.capturedAt || Date.now(),
    fbp: readCookie("_fbp") ?? previous.fbp,
    fbc: readCookie("_fbc") ?? previous.fbc,
    ttp: readCookie("_ttp") ?? previous.ttp,
    clientUserAgent:
      previous.clientUserAgent ?? cleanAttributionValue(navigator.userAgent),
    sourceUrl:
      previous.sourceUrl ??
      (current ? sanitizeAnalyticsUrl(current.toString()) : undefined),
  };

  if (current) {
    for (const parameterName of campaignParameterNames) {
      const value = cleanAttributionValue(current.searchParams.get(parameterName));
      if (value) next[parameterName] = value;
    }
  }

  const fbclid = cleanAttributionValue(current?.searchParams.get("fbclid"));
  if (!next.fbc && fbclid) {
    next.fbc = `fb.1.${Date.now()}.${fbclid}`.slice(0, 200);
  }

  const ttclid = cleanAttributionValue(current?.searchParams.get("ttclid"));
  if (ttclid) next.ttclid = ttclid;

  try {
    window.localStorage.setItem(attributionStorageKey, JSON.stringify(next));
  } catch {
    // Storage can be unavailable in private browsing contexts.
  }

  return next;
}

export function getCheckoutAttribution(): CheckoutAttribution | undefined {
  const current = currentLocationUrl();
  const attribution = captureAttribution();
  const hasCampaignSignal =
    Boolean(attribution.fbp || attribution.fbc) ||
    campaignParameterNames.some((parameterName) => Boolean(attribution[parameterName]));
  if (!current || (!isProductionHost() && !hasCampaignSignal)) return undefined;

  return {
    fbp: attribution.fbp,
    fbc: attribution.fbc,
    clientUserAgent: attribution.clientUserAgent,
    sourceUrl: sanitizeAnalyticsUrl(current.toString()),
    ttp: attribution.ttp,
    ttclid: attribution.ttclid,
    ...campaignParameterNames.reduce<AnalyticsCampaignProperties>(
      (result, parameterName) => {
        const value = attribution[parameterName];
        if (value) result[parameterName] = value;
        return result;
      },
      {},
    ),
  };
}

function attributionProperties(): AnalyticsCampaignProperties {
  const attribution = captureAttribution();
  return campaignParameterNames.reduce<AnalyticsCampaignProperties>(
    (result, parameterName) => {
      const value = attribution[parameterName];
      if (value) result[parameterName] = value;
      return result;
    },
    {},
  );
}

function whenPostHogReady(
  callback: (posthog: PostHogModule["default"]) => void,
) {
  posthogModulePromise
    ?.then((posthogModule) => {
      if (posthogModule) callback(posthogModule.default);
    })
    .catch(() => {
      // Analytics failures must never affect the product shell.
    });
}

export function trackEvent(
  event: string,
  properties: AnalyticsProperties = {},
) {
  if (typeof window === "undefined") return;

  const payload = {
    ...attributionProperties(),
    ...properties,
    page_path: sanitizeAnalyticsPath(window.location.pathname),
    page_url: sanitizeAnalyticsUrl(window.location.href),
  };

  window.dataLayer?.push({ event, ...payload });
  window.gtag?.("event", event, payload);
  whenPostHogReady((posthog) => posthog.capture(event, payload));
}

export function initAnalytics() {
  if (typeof window === "undefined") return;

  captureAttribution();

  if (
    googleMeasurementId &&
    !document.querySelector(`script[data-ga4="${googleMeasurementId}"]`)
  ) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleMeasurementId)}`;
    script.dataset.ga4 = googleMeasurementId;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer ?? [];
    window.gtag =
      window.gtag ??
      function gtag(...args: unknown[]) {
        window.dataLayer?.push(args);
      };
    window.gtag("js", new Date());
    window.gtag("config", googleMeasurementId, { send_page_view: false });
  }

  initializeMetaPixel();
  initializeTikTokPixel();

  if (posthogKey && !posthogModulePromise) {
    posthogModulePromise = import("posthog-js/dist/module.slim")
      .then((posthogModule) => {
        const { default: posthog } = posthogModule;
        posthog.init(posthogKey, {
          api_host: posthogHost,
          defaults: "2026-05-30",
          autocapture: false,
          capture_pageview: false,
          disable_session_recording: true,
          disable_surveys: true,
          advanced_disable_flags: true,
          before_send: (capture) => {
            if (!capture) return capture;
            const properties = { ...capture.properties };
            for (const key of ["$current_url", "$initial_referrer", "$referrer"]) {
              const value = properties[key];
              if (typeof value === "string") {
                properties[key] = sanitizeAnalyticsUrl(value);
              }
            }
            if (typeof properties.$pathname === "string") {
              properties.$pathname = sanitizeAnalyticsPath(properties.$pathname);
            }
            return { ...capture, properties };
          },
        });
        return posthogModule;
      })
      .catch(() => {
        posthogModulePromise = undefined;
        return undefined;
      });
  }
}

function initializeTikTokPixel() {
  if (!isProductionHost() || !tiktokPixelId) return false;

  if (!window.ttq || typeof window.ttq.load !== "function") {
    window.TiktokAnalyticsObject = "ttq";
    const ttq = [] as unknown as TikTokPixel;
    ttq._i = {};
    ttq._t = {};
    ttq._o = {};
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
      "holdConsent",
      "revokeConsent",
      "grantConsent",
    ];
    ttq.setAndDefer = (target, method) => {
      const targetRecord = target as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >;
      targetRecord[method] = (...args: unknown[]) => {
        target.push?.([method, ...args]);
      };
    };
    for (const method of ttq.methods) ttq.setAndDefer(ttq, method);
    ttq.instance = (id) => {
      ttq._i ??= {};
      ttq._i[id] ??= [] as unknown as TikTokPixel;
      for (const method of ttq.methods ?? []) {
        ttq.setAndDefer?.(ttq._i[id], method);
      }
      return ttq._i[id];
    };
    ttq.load = (id, options) => {
      const scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i ??= {};
      ttq._i[id] = [] as unknown as TikTokPixel;
      (ttq._i[id] as unknown as { _u?: string })._u = scriptUrl;
      ttq._t ??= {};
      ttq._t[id] = +new Date();
      ttq._o ??= {};
      ttq._o[id] = options ?? {};

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = `${scriptUrl}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    };
    window.ttq = ttq;
  }

  if (!window.__stalkieTikTokPixelInitialized) {
    window.ttq.load?.(tiktokPixelId);
    window.ttq.page?.();
    window.__stalkieTikTokPixelInitialized = true;
  }

  return true;
}

function trackTikTokEvent(
  eventName: string,
  properties: AnalyticsProperties = {},
) {
  if (!initializeTikTokPixel()) return;
  window.ttq?.track?.(eventName, properties);
}

export function initializeMetaPixel() {
  if (!isProductionHost()) return false;

  const pixelId = metaPixelId();
  if (!pixelId) return false;

  if (!window.fbq) {
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue?.push(args);
    }) as MetaPixel;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!window.__stalkieMetaPixelInitialized) {
    window.fbq("init", pixelId);
    window.__stalkieMetaPixelInitialized = true;
  }

  if (!document.querySelector("script[data-stalkie-meta-pixel]")) {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.dataset.stalkieMetaPixel = "true";
    document.head.appendChild(script);
  }

  return true;
}

export function trackMetaEvent(
  name: string,
  params: AnalyticsProperties = {},
  options?: { eventId?: string },
) {
  if (!initializeMetaPixel()) return;

  const eventId = options?.eventId?.trim();
  if (eventId) {
    window.fbq?.("track", name, params, { eventID: eventId });
    return;
  }
  window.fbq?.("track", name, params);
}

export function isMetaPixelPageViewAllowed(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/pt/" ||
    pathname === "/es/" ||
    pathname.startsWith("/how-to-see-who-someone-followed-on-instagram") ||
    pathname.startsWith("/instagram-follow-tracker") ||
    pathname.startsWith("/track-instagram-following-activity") ||
    pathname.startsWith("/anonymous-instagram-tracker") ||
    pathname.startsWith("/instagram-follow-notifications") ||
    pathname.startsWith("/instagram-unfollow-tracker") ||
    pathname.startsWith("/instagram-following-list-changes")
  );
}

export function trackPageView(routePath: string) {
  const path = sanitizeAnalyticsPath(routePath);
  trackEvent("page_view", { route_path: path });
  if (initializeTikTokPixel()) window.ttq?.page?.();
  if (isMetaPixelPageViewAllowed(routePath)) {
    trackMetaEvent("PageView", { content_name: path });
  }
}

export function identifyAnalytics(userId: string) {
  const stableUserId = userId.trim();
  if (!stableUserId) return;
  whenPostHogReady((posthog) => posthog.identify(stableUserId));
}

export function resetAnalytics() {
  whenPostHogReady((posthog) => posthog.reset());
}

export function trackCheckoutInitiated(args: {
  sessionId: string;
  plan: "weekly" | "monthly";
  value: number;
  currency: string;
}) {
  const eventId = args.sessionId.trim();
  if (!eventId) return;

  trackEvent("stalkie_checkout_started", {
    plan: args.plan,
    value: args.value,
    currency: args.currency.toUpperCase(),
  });
  trackMetaEvent(
    "InitiateCheckout",
    {
      value: args.value,
      currency: args.currency.toUpperCase(),
      content_name: "stalkie_subscription",
      content_type: "product",
    },
    { eventId: `stripe:${eventId}` },
  );
  trackTikTokEvent("InitiateCheckout", {
    content_id: "stalkie_subscription",
    content_type: "product",
    plan: args.plan,
    value: args.value,
    currency: args.currency.toUpperCase(),
  });
}

export function trackBrowserPurchase(args: {
  transactionId: string;
  value: number;
  currency: string;
  plan?: "weekly" | "monthly";
}) {
  const transactionId = args.transactionId.trim();
  if (!transactionId || !Number.isFinite(args.value) || args.value <= 0) return;

  const dedupeKey = `stalkie:analytics:purchase:${transactionId}`;
  try {
    if (window.sessionStorage.getItem(dedupeKey)) return;
    window.sessionStorage.setItem(dedupeKey, "1");
  } catch {
    // Continue without client-side deduplication if storage is unavailable.
  }

  trackEvent("subscription_purchase_confirmed", {
    plan: args.plan,
    value: args.value,
    currency: args.currency.toUpperCase(),
  });
  trackMetaEvent(
    "Purchase",
    {
      value: args.value,
      currency: args.currency.toUpperCase(),
      content_name: "stalkie_subscription",
      content_type: "product",
    },
    { eventId: `stripe:${transactionId}` },
  );
  trackTikTokEvent("Purchase", {
    event_id: `stripe:${transactionId}`,
    content_id: "stalkie_subscription",
    content_type: "product",
    plan: args.plan,
    value: args.value,
    currency: args.currency.toUpperCase(),
  });
}
